/**
 * RecordReference — a tiptap inline atom node for Auger-flavoured markdown
 * record references.
 *
 * Serializes to/from the `[[<record_links.id>|<Display>]]` markdown token: the
 * id is the junction row id (stable across merges/renames), the display text is
 * a cached label. `@tiptap/markdown` reads the markdown handlers off the
 * top-level config fields (markdownTokenName / markdownTokenizer / parseMarkdown
 * / renderMarkdown), so any editor that includes both this node and the Markdown
 * extension round-trips the token automatically.
 */

import { mergeAttributes, Node, VueNodeViewRenderer } from "@tiptap/vue-3"
import RecordReferenceChip from "./RecordReferenceChip.vue"

// Matches `[[42]]` or `[[42|Acme Corp]]` at the start of the input. The
// separator may be escaped (`[[42\|Acme]]`) — required inside a table cell,
// where a bare `|` would split the cell.
const TOKEN_RE = /^\[\[(\d+)(?:\\?\|([^\]]*))?\]\]/

/**
 * Make a cached display label safe inside the token: `]` would end it early
 * and `|` would be read as a separator. The label is only a fallback — the
 * live record name wins at render time — so a lossy substitution is fine.
 */
export function sanitizeReferenceLabel(label: string): string {
  return label.replace(/\[/g, "(").replace(/\]/g, ")").replace(/\|/g, "/")
}

interface ReferenceToken {
  type: "recordReference"
  raw: string
  linkId: number
  label: string
}

/**
 * Parse a leading `[[id|label]]` reference token. Returns undefined when the
 * input doesn't start with one. Shared by the marked tokenizer below and unit
 * tests.
 */
export function tokenizeReference(src: string): ReferenceToken | undefined {
  const m = TOKEN_RE.exec(src)
  if (!m) return undefined
  return { type: "recordReference", raw: m[0], linkId: Number(m[1]), label: m[2] ?? "" }
}

export const RecordReference = Node.create({
  name: "recordReference",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      linkId: {
        default: null,
        parseHTML: (el) => Number(el.getAttribute("data-link-id")) || null,
        renderHTML: (attrs) => (attrs.linkId ? { "data-link-id": String(attrs.linkId) } : {}),
      },
      label: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-label") || el.textContent || "",
        renderHTML: (attrs) => (attrs.label ? { "data-label": attrs.label } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: "span[data-link-id]" }]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { class: "record-chip" }),
      node.attrs.label || `#${node.attrs.linkId}`,
    ]
  },

  addNodeView() {
    return VueNodeViewRenderer(RecordReferenceChip)
  },

  // ── Markdown round-trip (read by @tiptap/markdown) ──────────────────
  markdownTokenName: "recordReference",

  markdownTokenizer: {
    name: "recordReference",
    level: "inline",
    start: (src: string) => {
      const i = src.indexOf("[[")
      return i === -1 ? src.length : i
    },
    tokenize: (src: string) => tokenizeReference(src),
  },

  parseMarkdown: (token, helpers) =>
    helpers.createNode("recordReference", { linkId: token.linkId, label: token.label }),

  renderMarkdown: (node) => {
    const linkId = node.attrs?.linkId
    const label = sanitizeReferenceLabel(node.attrs?.label ?? "")
    return label ? `[[${linkId}|${label}]]` : `[[${linkId}]]`
  },
})
